import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  InputGroup,
  NON_EU_COUNTRIES,
  NumericInputGroup,
  useAddressLatinCharacterValidation
} from '@/common'
import { InstallationAddress } from '@/features/installations/api/InstallationModel'
import { useSession } from '@/shared/hooks/useSession'
import UnlocodeInput from '@/components/UnlocodeInput'

function AddressForm({
  street,
  street2,
  country,
  streetNumber,
  postCode,
  poBox,
  city,
  onInputChange
}: {
  street?: string
  street2?: string
  country: string
  streetNumber?: string
  postCode?: string
  poBox?: string
  city?: string
  onInputChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
}) {
  const { t } = useTranslation()
  const label = (key: string) => t(`installations:fields.${key}.label`)

  const addressErrors = useAddressLatinCharacterValidation(
    {
      street,
      street2,
      streetNumber,
      postCode,
      poBox,
      city
    },
    t('common:errors.latinCharacters'),
    undefined
  )

  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <InputGroup
            id="street"
            label={label('street')}
            value={street}
            onChange={onInputChange}
            error={addressErrors.street || undefined}
          />
        </div>

        <InputGroup
          id="streetNumber"
          label={label('streetNumber')}
          value={streetNumber}
          onChange={onInputChange}
          error={addressErrors.streetNumber || undefined}
        />
      </div>

      <InputGroup
        id="street2"
        label={label('street2')}
        value={street2}
        onChange={onInputChange}
        tooltip={t('installations:fields.street2.tooltip')}
        tooltipPlacement="top"
        error={addressErrors.street2 || undefined}
      />

      <div className="grid md:grid-cols-3 gap-4">
        <InputGroup
          id="postCode"
          label={label('postCode')}
          value={postCode}
          onChange={onInputChange}
          tooltip={t('installations:fields.postCode.tooltip')}
          tooltipPlacement="top"
          required={
            NON_EU_COUNTRIES.find(countryCode => countryCode.value === country)?.isPostCodeRequired
          }
          error={addressErrors.postCode || undefined}
        />
        <InputGroup
          id="city"
          label={label('city')}
          value={city}
          onChange={onInputChange}
          required
          error={addressErrors.city || undefined}
        />
        <InputGroup
          id="poBox"
          label={label('poBox')}
          value={poBox}
          onChange={onInputChange}
          error={addressErrors.poBox || undefined}
        />
      </div>
    </>
  )
}

export default function AddressCard({
  id,
  onChange,
  state
}: {
  id: string
  onChange: (payload: InstallationAddress) => void
  state: InstallationAddress
}) {
  const {
    session: {
      organization: { placeOfRegistration: organizationPlaceOfRegistration }
    }
  } = useSession()

  const { t } = useTranslation()
  const {
    street,
    street2,
    country,
    streetNumber,
    postCode,
    poBox,
    city,
    unlocode,
    longitude,
    latitude
  } = state

  /* ────────────────── Validation helpers ────────────────── */
  const longitudeError = useMemo(() => {
    if (longitude === null || longitude === undefined || String(longitude).trim() === '') {
      return undefined
    }
    const numericValue = Number(longitude)
    return numericValue < -180 || numericValue > 180
      ? t('installations:fields.longitude.errors.outOfRange', { min: -180, max: 180 })
      : undefined
  }, [longitude, t])

  const latitudeError = useMemo(() => {
    if (latitude === null || latitude === undefined || String(latitude).trim() === '') {
      return undefined
    }
    const numericValue = Number(latitude)
    return numericValue < -90 || numericValue > 90
      ? t('installations:fields.latitude.errors.outOfRange', { min: -90, max: 90 })
      : undefined
  }, [latitude, t])

  const label = (key: string) => t(`installations:fields.${key}.label`)
  const countryFullName = (country: string) => {
    return country ? t(`countriesByIsoAlpha2:${country}`) : ''
  }

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...state, [evt.target.id]: evt.target.value })
    },
    [state, onChange]
  )

  const onUnLocodeChange = useCallback(
    (value: string) => {
      onChange({ ...state, unlocode: value })
    },
    [state, onChange]
  )

  return (
    <div id={id} className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle title={t('installations:create.sections.address.title')}></CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <AddressForm
            street={street}
            street2={street2}
            country={country}
            streetNumber={streetNumber}
            postCode={postCode}
            poBox={poBox}
            city={city}
            onInputChange={onInputChange}
          />
          <InputGroup
            id="country"
            label={label('country')}
            value={countryFullName(country)}
            disabled
          />
          <UnlocodeInput
            countryCodeFilter={organizationPlaceOfRegistration}
            label={label('unlocode')}
            value={unlocode || ''}
            tooltip={t('installations:fields.unlocode.tooltip')}
            tooltipPlacement="top"
            onChange={value => {
              onUnLocodeChange(value || '')
            }}
          />
          <div className="grid grid-cols-3 gap-4">
            <NumericInputGroup
              id="longitude"
              label={label('longitude')}
              value={longitude ? longitude.toString() : ''}
              onChange={onInputChange}
              tooltip={t('installations:fields.longitude.tooltip')}
              tooltipPlacement="top"
              error={longitudeError}
              numericFormat={{
                decimalScale: 6,
                min: -180,
                max: 180,
                fixedDecimalScale: true
              }}
            />
            <NumericInputGroup
              id="latitude"
              label={label('latitude')}
              value={latitude ? latitude.toString() : ''}
              onChange={onInputChange}
              tooltip={t('installations:fields.latitude.tooltip')}
              tooltipPlacement="top"
              error={latitudeError}
              numericFormat={{
                decimalScale: 6,
                min: -90,
                max: 90,
                fixedDecimalScale: true
              }}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
